import React, { useMemo, useState } from 'react';
import { Bot, Trash2, CheckCircle2, Plus, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { STATUS_LABELS, SIGNATURE_LABELS, fmtDate } from './api';
import { PackVersions } from './PackVersions';

const SORTS = {
  recent: { label: 'Tri : plus récentes', fn: (a, b) => (b.created_at || '').localeCompare(a.created_at || '') },
  ancien: { label: 'Tri : plus anciennes', fn: (a, b) => (a.created_at || '').localeCompare(b.created_at || '') },
  manquants: { label: 'Tri : plus de manquants', fn: (a, b) => b.nb_manquants - a.nb_manquants },
  demandeur: { label: 'Tri : demandeur A→Z', fn: (a, b) => (a.demandeur || '').localeCompare(b.demandeur || '') },
};

const SessionItem = ({ s, active, onSelect, onDelete, onVersion }) => {
  const st = STATUS_LABELS[s.status] || STATUS_LABELS.NOUVEAU;
  const sig = s.simulation_reference ? SIGNATURE_LABELS[s.signature?.etat] : null;
  return (
    <div
      role="button" tabIndex={0} onClick={() => onSelect(s.id)} onKeyDown={(e) => e.key === 'Enter' && onSelect(s.id)}
      className={`rounded-lg border p-3 cursor-pointer transition-colors ${active ? 'border-violet-400 bg-violet-50/60' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
      data-testid={`ia-session-${s.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{s.reference}</p>
          {s.demandeur && <p className="text-xs text-slate-600 truncate">{s.demandeur}</p>}
          <p className="text-[11px] text-slate-400">{fmtDate(s.created_at)}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }} className="text-slate-300 hover:text-red-500" aria-label="Supprimer la session" data-testid={`ia-session-delete-${s.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
        <span className="text-[11px] text-slate-400">{s.nb_documents} doc(s)</span>
        {s.nb_manquants > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-500 text-white">{s.nb_manquants} manquant(s)</span>}
        {sig && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sig.cls}`} data-testid={`ia-session-signature-${s.id}`}>{sig.label}</span>}
      </div>
      {s.simulation_reference && <p className="text-[11px] text-violet-600 mt-1">→ {s.simulation_reference}</p>}
      {s.completion_sent_to && (
        <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Envoyé à {s.completion_sent_to}</p>
      )}
      <PackVersions versions={s.pack_versions} onAction={(action, v) => onVersion(s.id, action, v)} />
    </div>
  );
};

export const SessionsSidebar = ({ sessions, currentId, onSelect, onCreate, onDelete, onVersion }) => {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent');
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? sessions.filter((s) => [s.reference, s.demandeur, s.siret, s.simulation_reference].some((v) => (v || '').toLowerCase().includes(t))) : sessions;
    return [...list].sort(SORTS[sort].fn);
  }, [sessions, q, sort]);

  return (
    <aside className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 h-fit" data-testid="ia-sessions-sidebar">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Bot className="h-4 w-4 text-violet-600" /> Sessions</h4>
        <Button size="sm" variant="outline" onClick={onCreate} className="h-8 gap-1" data-testid="ia-session-new-btn"><Plus className="h-3.5 w-3.5" /> Nouveau</Button>
      </div>
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, n°, SIRET…)" className="h-8 pl-8 text-xs" data-testid="ia-session-search" />
      </div>
      <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="ia-session-sort">
        {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      {shown.length === 0 && <p className="text-xs text-slate-400">{sessions.length ? 'Aucun résultat.' : 'Aucune session. Cliquez sur « Nouveau ».'}</p>}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto">
        {shown.map((s) => (
          <SessionItem key={s.id} s={s} active={s.id === currentId} onSelect={onSelect} onDelete={onDelete} onVersion={onVersion} />
        ))}
      </div>
    </aside>
  );
};
